import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FileParameter, IAttachmentDto } from '@fdm/api-client';
import { FileUploadComponent, TimelineRecord } from '@fdm/shared';
import {
  AngularEditorConfig,
  AngularEditorModule,
} from '@kolkov/angular-editor';
import { createEditorConfigs } from '../../../reports/components/report-form/shared/richtext-editor-config-helper';

export interface CreateNoteDialogResponse {
  title: string;
  text: string;
  files: FileParameter[];
  deleteAttachmentIds: number[];
}

@Component({
  selector: 'app-create-note-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AngularEditorModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatIcon,
    MatButton,
    FileUploadComponent,
  ],
  templateUrl: './create-note-dialog.component.html',
  styleUrl: './create-note-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateNoteDialog implements OnInit {
  private _data = inject<TimelineRecord>(MAT_DIALOG_DATA);

  readonly _dialogRef = inject(MatDialogRef<CreateNoteDialog, CreateNoteDialogResponse>);

  private _editorConfigs = createEditorConfigs(3, 'general');

  public configEN: AngularEditorConfig = this._editorConfigs[0];

  public isEditing = false;
  public files: Partial<FileParameter>[] = [];
  public attachments: IAttachmentDto[] = [];
  deleteAttachmentIds: number[] = [];

  form = new FormGroup({
    title: new FormControl<string | null>(null, Validators.required),
    text: new FormControl<string | null>(null, Validators.required),
  });

  public get showAddFileBtn() {
    const haveAttachments = this.attachments.some((item) => item.fileName);
    const haveFiles = this.files.some((item) => item.fileName);

    return haveAttachments || haveFiles;
  }

  ngOnInit(): void {
    this.attachments = this._data?.attachments ?? [];

    this._checkFiles();

    if (this._data) {
      this.isEditing = true;

      this.form.patchValue({
        ...this._data,
        text: this._data.previewText,
      });
    }
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }
    this._dialogRef.close({
      ...this.form.value,
      files: this.files.filter(file => file.fileName),
      deleteAttachmentIds: this.deleteAttachmentIds,
    });
  }

  deleteFile(id: number) {
    this.attachments = this.attachments.filter((file) => file.id !== id);
    this.deleteAttachmentIds.push(id);
    this._checkFiles();
  }

  removeFileFromList(idx: number) {
    this.files.splice(idx, 1);
    this._checkFiles();
  }

  private _checkFiles() {
    const noAttachments = this.attachments.length === 0;
    const noFiles = this.files.length === 0;

    if (noAttachments && noFiles) {
      this.files = [{ fileName: '' }];
    }
  }

  getFile(file: FileParameter, idx: number) {
    this.files[idx] = file;
  }

  addFile() {
    this.files.push({ fileName: '' });
  }
}
